import { Component, inject, signal, OnInit, PLATFORM_ID, computed } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { GoLiveService, GoLiveStatus, OwnerRow, PartnerGatewayRow } from '../../services/go-live';
import { ToastService } from '../../services/toast';

/**
 * Live / Test per client and per partner, with the readiness checklist that gates going live.
 *
 * Everyone starts in TEST. Going LIVE requires every check to pass — the server enforces this
 * too, so the button being disabled is a convenience, not the control. Dropping back to TEST is
 * always one click: stopping should never require passing a checklist.
 */
@Component({
  selector: 'app-go-live',
  standalone: true,
  imports: [CommonModule, ButtonModule, DialogModule, TagModule],
  template: `
    <div class="page">
      <div class="page-head">
        <div>
          <h2>Live &amp; Test Mode</h2>
          <p>Each client and partner runs independently — one can be live while another is still testing.</p>
        </div>
        <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary"
          size="small" [loading]="loading()" (onClick)="load()" />
      </div>

      <div class="summary">
        <div class="s-tile live"><span>Live</span><strong>{{ liveCount() }}</strong></div>
        <div class="s-tile test"><span>Test</span><strong>{{ testCount() }}</strong></div>
      </div>

      @for (grp of ['CLIENT', 'PARTNER']; track grp) {
        <div class="card grp">
          <h3>{{ grp === 'CLIENT' ? 'Clients' : 'Partners' }}</h3>
          @if (rowsFor(grp).length) {
            <table class="owners">
              <thead>
                <tr><th>Name</th><th>Code</th><th class="c">Mode</th><th class="r">Actions</th></tr>
              </thead>
              <tbody>
                @for (o of rowsFor(grp); track o.scope + '-' + o.ownerId) {
                  <tr [class.is-live]="o.mode === 'LIVE'">
                    <td class="nm">{{ o.name || '—' }}</td>
                    <td class="mono">{{ o.code || '—' }}</td>
                    <td class="c">
                      <p-tag [value]="o.mode" [severity]="o.mode === 'LIVE' ? 'success' : 'warn'" />
                    </td>
                    <td class="r">
                      <p-button label="Readiness" icon="pi pi-list-check" [text]="true" size="small"
                        (onClick)="open(o)" />
                      @if (o.mode === 'LIVE') {
                        <p-button label="Back to Test" icon="pi pi-undo" [text]="true" severity="secondary"
                          size="small" (onClick)="setMode(o, 'TEST')" />
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          } @else {
            <p class="empty">No {{ grp === 'CLIENT' ? 'clients' : 'partners' }} yet.</p>
          }
        </div>
      }

      <!-- ============ partner gateway assignment ============ -->
      <div class="card grp">
        <h3>Partner payment gateway</h3>
        <p class="grp-lede">
          Optional. A partner with no assignment uses the GLOBAL integration matching its own mode,
          which is the normal setup — one live row and one test row serve everybody. Assign one only
          when a partner must transact through a particular integration.
        </p>
        @if (partnerRows().length) {
          <table class="owners">
            <thead>
              <tr><th>Partner</th><th class="c">Mode</th><th>Assigned</th><th>Actually used</th><th class="r">Actions</th></tr>
            </thead>
            <tbody>
              @for (p of partnerRows(); track p.partnerId) {
                <tr>
                  <td class="nm">{{ p.partnerName || '—' }}<span class="sub">{{ p.partnerCode }}</span></td>
                  <td class="c"><p-tag [value]="p.mode" [severity]="p.mode === 'LIVE' ? 'success' : 'warn'" /></td>
                  <td>{{ p.integrationLabel || '— (global default)' }}</td>
                  <td>
                    @if (p.effectiveLabel) {
                      {{ p.effectiveLabel }}
                      <span class="src">{{ p.effectiveSource }}</span>
                    } @else {
                      <span class="none">No gateway for {{ p.mode }} mode — payments will fail</span>
                    }
                  </td>
                  <td class="r">
                    <p-button label="Assign" icon="pi pi-link" [text]="true" size="small"
                      (onClick)="openAssign(p)" />
                  </td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <p class="empty">No partners yet.</p>
        }
      </div>
    </div>

    <!-- ================= assign gateway ================= -->
    <p-dialog [header]="'Payment gateway — ' + (assignTarget()?.partnerName || '')"
      [visible]="assigning()" (visibleChange)="assigning.set($event)" [modal]="true" [draggable]="true"
      [style]="{ width: '520px' }">
      @if (assignTarget(); as t) {
        <p class="cur">
          This partner is in <p-tag [value]="t.mode" [severity]="t.mode === 'LIVE' ? 'success' : 'warn'" />
          — only a {{ t.mode }} integration will be used.
        </p>
        <label class="cf-label">Integration</label>
        <select class="cf-input" [value]="assignPick() ?? ''" (change)="assignPick.set($any($event.target).value ? +$any($event.target).value : null)">
          <option value="">— Use the global gateway for this mode —</option>
          @for (o of assignOptions(); track o.value) {
            <option [value]="o.value">{{ o.label }}</option>
          }
        </select>
        <p class="hint">
          Clearing the assignment is safe: the partner falls back to the global integration matching
          its mode.
        </p>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" [text]="true" severity="secondary" (onClick)="assigning.set(false)" />
        <p-button label="Save" icon="pi pi-check" [loading]="saving()" (onClick)="saveAssign()" />
      </ng-template>
    </p-dialog>

    <!-- ================= readiness ================= -->
    <p-dialog [header]="(status()?.name || 'Readiness') + ' — go live checklist'"
      [visible]="open$()" (visibleChange)="open$.set($event)" [modal]="true" [draggable]="true"
      [style]="{ width: '620px' }" [breakpoints]="{ '700px': '94vw' }">
      @if (checking()) {
        <div class="state"><i class="pi pi-spin pi-spinner"></i> Checking…</div>
      } @else if (status(); as s) {
        <div class="cur">
          Currently <p-tag [value]="s.mode" [severity]="s.mode === 'LIVE' ? 'success' : 'warn'" />
        </div>

        <ul class="checks">
          @for (c of s.checks; track c.key) {
            <li [class.ok]="c.passed">
              <i class="pi" [class.pi-check-circle]="c.passed" [class.pi-times-circle]="!c.passed"></i>
              <div>
                <strong>{{ c.label }}</strong>
                <span>{{ c.detail }}</span>
                @if (!c.passed) { <em>{{ c.fixHint }}</em> }
              </div>
            </li>
          }
        </ul>

        @if (s.canGoLive) {
          <div class="verdict ok"><i class="pi pi-check-circle"></i> All checks passed — this {{ s.scope === 'CLIENT' ? 'client' : 'partner' }} can go live.</div>
        } @else {
          <div class="verdict no">
            <i class="pi pi-lock"></i>
            Stays in test until every check passes. Enrolments made now are test data.
          </div>
        }
      }

      <ng-template pTemplate="footer">
        <p-button label="Close" [text]="true" severity="secondary" (onClick)="open$.set(false)" />
        @if (status(); as s) {
          @if (s.mode === 'TEST') {
            <p-button label="Go Live" icon="pi pi-bolt" severity="success"
              [disabled]="!s.canGoLive" [loading]="saving()" (onClick)="setMode(s, 'LIVE')" />
          } @else {
            <p-button label="Back to Test" icon="pi pi-undo" severity="secondary"
              [loading]="saving()" (onClick)="setMode(s, 'TEST')" />
          }
        }
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    :host { display: block; }
    .page-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
    .page-head h2 { font-size: 22px; font-weight: 700; margin: 0; color: var(--ink, #0f172a); }
    .page-head p { margin: 2px 0 0; color: var(--muted, #6b7280); font-size: 14px; }
    .card { background: #fff; border-radius: 16px; box-shadow: 0 4px 14px rgba(15,23,42,.05); }

    .summary { display: flex; gap: 12px; margin-bottom: 14px; }
    .s-tile {
      flex: 0 0 150px; background: #fff; border: 1px solid #eef2f6; border-radius: 14px;
      padding: 12px 16px; display: flex; flex-direction: column;
    }
    .s-tile span { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: var(--muted, #6b7280); }
    .s-tile strong { font-size: 24px; font-weight: 800; }
    .s-tile.live strong { color: #15803d; }
    .s-tile.test strong { color: #b45309; }

    .grp { padding: 16px 18px; margin-bottom: 14px; }
    .grp h3 { margin: 0 0 10px; font-size: 15px; font-weight: 700; }
    .empty { margin: 0; font-size: 13px; color: var(--muted, #6b7280); }
    .grp-lede { margin: 0 0 12px; font-size: 13px; color: var(--muted, #6b7280); max-width: 80ch; line-height: 1.5; }
    .nm .sub { display: block; font-weight: 400; font-size: 11.5px; color: var(--muted, #6b7280); }
    .src { display: block; font-size: 11px; color: var(--muted, #6b7280); }
    .none { color: #b91c1c; font-size: 12.5px; }
    .hint { font-size: 12px; color: var(--muted, #6b7280); margin: 8px 0 0; line-height: 1.45; }

    table.owners { width: 100%; border-collapse: collapse; }
    table.owners th {
      text-align: left; font-size: 11.5px; font-weight: 700; text-transform: uppercase;
      letter-spacing: .04em; color: var(--muted, #6b7280); padding: 6px 8px; border-bottom: 1px solid #eef2f6;
    }
    table.owners td { padding: 9px 8px; border-bottom: 1px solid #f5f8fa; font-size: 13px; vertical-align: middle; }
    table.owners tr:last-child td { border-bottom: 0; }
    .c { text-align: center; } .r { text-align: right; white-space: nowrap; }
    .nm { font-weight: 600; }
    .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12.5px; color: var(--brand); }

    .cur { font-size: 13.5px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
    .state { padding: 26px; text-align: center; color: var(--muted, #6b7280); }

    ul.checks { list-style: none; margin: 0 0 14px; padding: 0; display: flex; flex-direction: column; gap: 9px; }
    ul.checks li {
      display: flex; gap: 10px; align-items: flex-start; padding: 10px 12px;
      border: 1px solid #fecaca; background: #fef2f2; border-radius: 10px;
    }
    ul.checks li.ok { border-color: #bbf7d0; background: #f0fdf4; }
    ul.checks li > i { font-size: 15px; color: #dc2626; margin-top: 1px; }
    ul.checks li.ok > i { color: #16a34a; }
    ul.checks li div { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
    ul.checks strong { font-size: 13px; }
    ul.checks span { font-size: 12.5px; color: var(--muted, #6b7280); }
    ul.checks em { font-style: normal; font-size: 12px; color: #b91c1c; margin-top: 3px; }

    .verdict { display: flex; align-items: center; gap: 8px; font-size: 13px; padding: 10px 12px; border-radius: 10px; }
    .verdict.ok { background: #f0fdf4; border: 1px solid #86efac; color: #15803d; }
    .verdict.no { background: #fffbeb; border: 1px solid #fcd34d; color: #92400e; }

    @media (max-width: 700px) { .summary { flex-wrap: wrap; } }
  `],
})
export class GoLivePage implements OnInit {
  private svc = inject(GoLiveService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  readonly owners = signal<OwnerRow[]>([]);
  readonly loading = signal(false);
  readonly checking = signal(false);
  readonly saving = signal(false);
  readonly open$ = signal(false);
  readonly status = signal<GoLiveStatus | null>(null);

  readonly partnerRows = signal<PartnerGatewayRow[]>([]);
  readonly assigning = signal(false);
  readonly assignTarget = signal<PartnerGatewayRow | null>(null);
  readonly assignOptions = signal<{ value: number; label: string }[]>([]);
  readonly assignPick = signal<number | null>(null);

  readonly liveCount = computed(() => this.owners().filter((o) => o.mode === 'LIVE').length);
  readonly testCount = computed(() => this.owners().filter((o) => o.mode !== 'LIVE').length);

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
    this.loadPartnerGateways();
  }

  /** Which integration each partner is assigned, and which one it would actually use. */
  private loadPartnerGateways() {
    this.svc.partnerGateways().subscribe({
      next: (res: any) => this.partnerRows.set(res?.data ?? []),
      error: () => this.partnerRows.set([]),
    });
  }

  openAssign(p: PartnerGatewayRow) {
    this.assignTarget.set(p);
    this.assignPick.set(p.integrationId);
    this.assignOptions.set([]);
    this.assigning.set(true);
    this.svc.gatewayOptions(p.partnerId).subscribe({
      next: (res: any) => this.assignOptions.set(res?.data ?? []),
      error: () => this.assignOptions.set([]),
    });
  }

  saveAssign() {
    const t = this.assignTarget();
    if (!t) return;
    this.saving.set(true);
    this.svc.assignGateway(t.partnerId, this.assignPick()).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not assign the gateway.');
          return;
        }
        this.toast.success(res?.message || 'Gateway assigned.');
        this.assigning.set(false);
        this.loadPartnerGateways();
      },
      error: () => {
        this.saving.set(false);
        this.toast.error('Could not assign the gateway.');
      },
    });
  }

  rowsFor(scope: string): OwnerRow[] {
    return this.owners().filter((o) => o.scope === scope);
  }

  load() {
    this.loading.set(true);
    this.svc.owners().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        this.owners.set(res?.data ?? []);
      },
      error: () => {
        this.loading.set(false);
        this.toast.error('Could not load clients and partners.');
      },
    });
  }

  open(o: OwnerRow | GoLiveStatus) {
    this.status.set(null);
    this.checking.set(true);
    this.open$.set(true);
    this.svc.status(o.scope, o.ownerId).subscribe({
      next: (res: any) => {
        this.checking.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not run the checks.');
          this.open$.set(false);
          return;
        }
        this.status.set(res?.data ?? null);
      },
      error: () => {
        this.checking.set(false);
        this.open$.set(false);
        this.toast.error('Could not run the checks.');
      },
    });
  }

  setMode(o: OwnerRow | GoLiveStatus, mode: 'LIVE' | 'TEST') {
    this.saving.set(true);
    this.svc.setMode(o.scope, o.ownerId, mode).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          // The server re-runs the checks, so this fires if something changed since the dialog
          // was opened. Show the refreshed list rather than a bare failure.
          this.toast.error(res?.message || 'Could not change the mode.');
          if (res?.data?.checks && this.status()) {
            this.status.set({ ...this.status()!, checks: res.data.checks, canGoLive: false });
          }
          return;
        }
        this.toast.success(res?.message || 'Mode updated.');
        this.open$.set(false);
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.toast.error('Could not change the mode.');
      },
    });
  }
}
